import { AdminNotificationStatus } from '@repo/database/browser';
import type {
  AdminNotificationStats,
  FetchAdminNotificationsParams,
} from '@/api/admin-notifications';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
} from '@/lib/helpers/list-api-params';
import type { TabCounts, TranslatableDomainTab } from '@/lib/helpers/list-tabs';
import type { AdminNotificationsSearch } from '@/routes/-shared/adminNotificationsSearchSchema';

type AdminNotificationsApiParams = Omit<
  FetchAdminNotificationsParams,
  'skip' | 'take'
>;

export const NOTIFICATIONS_DEFAULT_TAB = 'all';

/**
 * Tab della lista al posto del vecchio filtro di stato (Previxio DS): Tutte ·
 * Bozze (contatore neutro) · Programmate (contatore informativo) · Inviate ·
 * Annullate. Il tab viaggia su `subTab`, lo stato per l'API si ricava dal tab.
 */
export const NOTIFICATION_TABS: readonly TranslatableDomainTab<AdminNotificationStatus>[] =
  [
    { value: NOTIFICATIONS_DEFAULT_TAB, text: 'adminNotifications.tabs.all' },
    {
      value: 'drafts',
      text: 'adminNotifications.tabs.drafts',
      filter: AdminNotificationStatus.DRAFT,
      countTone: 'neutral',
    },
    {
      value: 'scheduled',
      text: 'adminNotifications.tabs.scheduled',
      filter: AdminNotificationStatus.SCHEDULED,
      countTone: 'info',
    },
    {
      value: 'sent',
      text: 'adminNotifications.tabs.sent',
      filter: AdminNotificationStatus.SENT,
    },
    {
      value: 'cancelled',
      text: 'adminNotifications.tabs.cancelled',
      filter: AdminNotificationStatus.CANCELLED,
    },
  ];

/** Campi di `GET backoffice/admin-notifications` letti dalla search. */
const ADMIN_NOTIFICATIONS_API_PARAMS: ApiParamReaders<
  AdminNotificationsSearch,
  AdminNotificationsApiParams
> = SEARCH_AND_SORT_PARAMS;

/** Ricerca e ordinamento dalla search, lo stato dal tab (assente su «Tutte»). */
export function buildAdminNotificationsApiParams(
  search: AdminNotificationsSearch,
  status?: AdminNotificationStatus
): AdminNotificationsApiParams {
  return {
    ...pickApiParams(search, ADMIN_NOTIFICATIONS_API_PARAMS),
    ...(status === undefined ? {} : { status }),
  };
}

/**
 * Contatori dei tab: solo Bozze e Programmate, come il design (le altre
 * pill non si mostrano). Senza conteggi, nessuna pill.
 */
export function toNotificationTabCounts(
  stats: AdminNotificationStats | undefined
): TabCounts | undefined {
  if (!stats) return undefined;
  return {
    drafts: stats.byStatus.DRAFT,
    scheduled: stats.byStatus.SCHEDULED,
  };
}

/** Vuoto di ogni tab di stato senza ricerca; «Tutte» non ne ha. */
const NOTIFICATION_TAB_EMPTY_TITLE_KEYS: Readonly<
  Record<string, string | undefined>
> = {
  drafts: 'adminNotifications.empty.tabs.drafts',
  scheduled: 'adminNotifications.empty.tabs.scheduled',
  sent: 'adminNotifications.empty.tabs.sent',
  cancelled: 'adminNotifications.empty.tabs.cancelled',
};

/**
 * Titolo dello stato vuoto: «nessun risultato» solo con un termine di
 * ricerca; su un tab di stato senza ricerca il messaggio del tab («Nessuna
 * notifica inviata»); `undefined` su «Tutte» senza ricerca, dove resta il
 * vuoto vero con l'invio a tutti i tenant.
 */
export function getNotificationsEmptyTitleKey({
  search,
  activeTab,
}: {
  search: string | undefined;
  activeTab: string;
}): string | undefined {
  if (search) return 'adminNotifications.empty.no_results';
  return NOTIFICATION_TAB_EMPTY_TITLE_KEYS[activeTab];
}
